// src/sections/InfoNewsSection.tsx
import { useState, useEffect } from 'react';
import { Box, Typography, Button, Stack, Chip, Snackbar, Alert } from '@mui/material';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import { useSelector, useDispatch } from 'react-redux';
import { NewsCard, NewsList } from './components/news';
import { EventCard, EventsList } from './components/events';
import { ItemsModal } from '@/components/common/ItemsModal';
import MediaPreviewDialog from '@/components/common/MediaPreviewDialog';
import { AppDispatch } from '@/app/store';
import { fetchNewsArticles, fetchEventArticles, fetchNewsDocumentUrl } from '@/features/news/newsThunks';
import { selectVisibleNewsArticles, selectEventArticles, selectNewsLoading, selectEventLoading, selectNewsError, selectEventError } from '@/features/news/newsSelectors';
import { downloadFile } from '@/utils/downloadFile';
import { formatDate } from '@/utils/formatDate';

export default function InfoNewsSection() {
  const dispatch = useDispatch<AppDispatch>();

  const [newsModalOpen, setNewsModalOpen] = useState(false);
  const [eventsModalOpen, setEventsModalOpen] = useState(false);
  const [selectedNewsItem, setSelectedNewsItem] = useState<any>(null);
  const [selectedImageUrl, setSelectedImageUrl] = useState<string | null>(null);
  const [newsDetailModalOpen, setNewsDetailModalOpen] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertSeverity, setAlertSeverity] = useState<'success' | 'error' | 'warning' | 'info'>('info');
  const [imagePreviewOpen, setImagePreviewOpen] = useState(false);

  const news = useSelector(selectVisibleNewsArticles);
  const loadingNews = useSelector(selectNewsLoading);
  const errorNews = useSelector(selectNewsError);
  const events = useSelector(selectEventArticles);
  const loadingEvents = useSelector(selectEventLoading);
  const errorEvents = useSelector(selectEventError);

  useEffect(() => {
    dispatch(fetchNewsArticles());
    dispatch(fetchEventArticles());
  }, [dispatch]);

  const handleOpenNewsModal = () => setNewsModalOpen(true);
  const handleCloseNewsModal = () => setNewsModalOpen(false);

  const handleOpenEventsModal = () => setEventsModalOpen(true);
  const handleCloseEventsModal = () => setEventsModalOpen(false);

  const handleReadMore = (newsItem: any, imageUrl: string) => {
    setSelectedNewsItem(newsItem);
    setSelectedImageUrl(imageUrl || '/images/Logo.jpg');
    setNewsDetailModalOpen(true);
  };

  const handleCloseNewsDetailModal = () => {
    setNewsDetailModalOpen(false);
    setSelectedNewsItem(null);
    setSelectedImageUrl(null);
  };

  const handleCloseAlert = () => {
    setAlertOpen(false);
  };

  const showAlert = (message: string, severity: 'success' | 'error' | 'warning' | 'info') => {
    setAlertMessage(message);
    setAlertSeverity(severity);
    setAlertOpen(true);
  };

  const handleDownloadDocument = async () => {
    if (!selectedNewsItem?.docName) {
      console.error('No document name provided');
      return;
    }

    try {
      const STORAGE_PATH = 'announcements/';
      const path = STORAGE_PATH + selectedNewsItem.docName;
      const downloadUrl = await dispatch(fetchNewsDocumentUrl({ storagePath: path })).unwrap();
      await downloadFile(downloadUrl, selectedNewsItem.docName || 'documento');
    } catch (err) {
      console.error('Error downloading document:', err);
      showAlert('Error al descargar el documento. Por favor, intente nuevamente.', 'error');
    }
  };

  const handleEventClick = (event: any) => {
    console.log('Event clicked:', event);
    // Navegar a página de detalle
  };

  const detailImageUrl = selectedImageUrl || '/images/Logo.jpg';
  const isDetailImageFallback = detailImageUrl === '/images/Logo.jpg';

  return (
    <>
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: '1.4fr 0.2fr', lg: '1.8fr 1fr' },
          alignItems: 'start',
          px: { xs: 2, sm: 3, md: 6, lg: 8 },
          pt: { xs: 6, sm: 7, md: 8 },
          gap: { xs: 2, sm: 2.5, md: 3 },
          mb: { xs: 6, sm: 7, md: 8 }
        }}
      >
        {/* Sección de Últimas Noticias */}
        <Box sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              mb: { xs: 2, sm: 3 },
              width: "100%",
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 600, md: 700 },
                mb: { xs: 1, sm: 0 }
              }}
            >
              Últimas Noticias y Comunicados
            </Typography>
            <Button
              sx={{
                color: 'primary.500',
                border: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                p: { xs: '8px 12px', sm: '4px 8px' },
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 0.85,
                  transform: 'translateX(4px)',
                },
              }}
              onClick={handleOpenNewsModal}
            >
              VER TODO
            </Button>
          </Box>

          <NewsList
            news={news}
            loading={loadingNews}
            error={errorNews}
            limit={3}
            variant="default"
            onReadMore={(newsItem, imageUrl) => handleReadMore(newsItem, imageUrl)}
          />
        </Box>

        {/* Sección de Próximos Eventos */}
        <Box sx={{ justifyContent: "center" }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'flex-start', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'space-between' },
              mb: { xs: 2, sm: 3 },
              width: "100%",
              px: { xs: 0, sm: 2, md: 4 }
            }}
          >
            <Typography
              variant="h5"
              sx={{
                fontSize: { xs: '1.125rem', sm: '1.25rem', md: '1.5rem' },
                fontWeight: { xs: 600, md: 700 },
                mb: { xs: 1, sm: 0 }
              }}
            >
              Próximos Eventos
            </Typography>
            <Button
              sx={{
                color: 'primary.500',
                border: 'none',
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                cursor: 'pointer',
                p: { xs: '8px 12px', sm: '4px 8px' },
                transition: 'all 0.2s ease',
                '&:hover': {
                  opacity: 0.85,
                  transform: 'translateX(4px)',
                },
              }}
              onClick={handleOpenEventsModal}
            >
              VER TODO
            </Button>
          </Box>

          <EventsList
            events={events}
            loading={loadingEvents}
            error={errorEvents}
            limit={2}
            variant="default"
            onClick={handleEventClick}
          />
        </Box>
      </Box>

      {/* Modal de Noticias */}
      <ItemsModal
        open={newsModalOpen}
        onClose={handleCloseNewsModal}
        title="Todas las Noticias"
      >
        <Stack spacing={3}>
          {news.map((newsItem, index) => (
            <NewsCard
              // newsItem.id || 
              key={index}
              newsItem={newsItem}
              variant="modal"
              onReadMore={(imageUrl) => handleReadMore(newsItem, imageUrl)}
            />
          ))}
        </Stack>
      </ItemsModal>

      {/* Modal de Eventos */}
      <ItemsModal
        open={eventsModalOpen}
        onClose={handleCloseEventsModal}
        title="Todos los Eventos"
        isEvent={true}
      >
        <Stack spacing={3}>
          {events.map((event, index) => (
            <EventCard
              // event.id ||
              key={index}
              event={event}
              variant="modal"
              onClick={() => handleEventClick(event)}
            />
          ))}
        </Stack>
      </ItemsModal>

      {/* Modal de Detalle de Noticia */}
      <ItemsModal
        open={newsDetailModalOpen}
        onClose={handleCloseNewsDetailModal}
        title={selectedNewsItem?.title || 'Detalle de Noticia'}
        showDownloadButton={selectedNewsItem?.isNews === false && !!selectedNewsItem?.docName}

        onDownload={handleDownloadDocument}
      >
        {selectedNewsItem && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: 'center',
              gap: { xs: 2, sm: 3, md: 4 },
            }}
          >
            <Box
              component="img"
              src={detailImageUrl}
              alt={selectedNewsItem.title}
              onClick={() => {
                if (!isDetailImageFallback) setImagePreviewOpen(true);
              }}
              onError={(e: any) => {
                e.target.src = '/images/Logo.jpg';
              }}
              sx={{
                width: isDetailImageFallback
                  ? { xs: 160, sm: 180, md: 220 }
                  : { xs: '100%', sm: 360, md: 480, lg: 560 },
                maxHeight: isDetailImageFallback
                  ? { xs: 160, sm: 180, md: 220 }
                  : { xs: 360, sm: 420, md: 520, lg: 600 },
                objectFit: 'contain',
                backgroundColor: '#f9fafb',
                borderRadius: 2,
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
                flexShrink: 0,
                cursor: isDetailImageFallback ? 'default' : 'zoom-in',
                transition: 'transform 200ms ease, box-shadow 200ms ease',
                '&:hover': isDetailImageFallback
                  ? undefined
                  : {
                      transform: 'translateY(-2px)',
                      boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
                    },
              }}
            />
            <Box sx={{ flex: 1, padding: { xs: 0, sm: '1rem', md: '1.5rem' } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2, mb: 2 }}>
                <Chip
                  label={selectedNewsItem.tag}
                  sx={{
                    backgroundColor: '#E8F5F3',
                    color: 'primary.500',
                  }}
                />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <CalendarTodayOutlinedIcon sx={{ fontSize: 18, color: '#6b7280' }} />
                  <Typography variant="body2" sx={{ color: '#6b7280' }}>
                    {formatDate(selectedNewsItem.date)}
                  </Typography>
                </Box>
              </Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3 }}>
                {selectedNewsItem.summary}
              </Typography>
              <Typography variant="body1" sx={{ color: '#374151', lineHeight: 1.8, textAlign: 'left', whiteSpace: 'pre-line' }}>
                {selectedNewsItem.content?.replace(/\n/g, '\n\n')}
              </Typography>
            </Box>
          </Box>
        )}
      </ItemsModal>

      {/* Vista expandida de la imagen principal de la noticia */}
      <MediaPreviewDialog
        open={imagePreviewOpen}
        onClose={() => setImagePreviewOpen(false)}
        src={detailImageUrl}
        type="image"
        name={selectedNewsItem?.title}
      />

      {/* Alert Snackbar */}
      <Snackbar
        open={alertOpen}
        autoHideDuration={4000}
        onClose={handleCloseAlert}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseAlert}
          severity={alertSeverity}
          sx={{ width: "100%" }}
        >
          {alertMessage}
        </Alert>
      </Snackbar>
    </>
  );
}